import { useState } from 'react';
import type { TerminalParamScope } from '@reform-lite/contracts';
import { useT } from '@/i18n/use-t';
import { useHasPermission } from '@/auth/auth-store';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { extractApiError } from '@/lib/api-error';
import { useExportTerminalParams } from '@/hooks/use-terminal-params';
import { useAllTerminals, useTerminalProfiles } from '@/hooks/use-terminals';
import { ImportPanel } from './import-panel';
import { TerminalAudioPanel } from './terminal-audio-panel';
import { ParameterEditor, type EditorTarget } from './parameter-editor';

/**
 * Settings → Parameters (RL-273, TP-FR-009).
 *
 * The full editor over every parameter the registry declares, behind a scope
 * selector: global, one profile, or one terminal. The two in-context
 * placements — the profile page and the terminal page — render the same
 * `ParameterEditor` pinned to their scope; this page is the only one that
 * lets the operator choose.
 */
export function ParametersPage() {
  const t = useT();
  const canRead = useHasPermission('terminal-params.read');
  const canWrite = useHasPermission('terminal-params.write');
  const [scope, setScope] = useState<TerminalParamScope>('global');
  const [importing, setImporting] = useState(false);
  const [managingAudio, setManagingAudio] = useState(false);
  const [profileId, setProfileId] = useState('');
  const [terminalId, setTerminalId] = useState('');
  const profiles = useTerminalProfiles(canRead && scope !== 'global');
  const terminals = useAllTerminals(canRead && scope === 'terminal');
  const exportParams = useExportTerminalParams();
  const [exchangeError, setExchangeError] = useState<string | null>(null);

  if (!canRead) {
    return (
      <section>
        <h1 className="text-2xl font-semibold">{t('parameters.page.title', 'Parameters')}</h1>
        <p className="mt-3 text-sm text-destructive">
          {t('parameters.page.noAccess', 'You do not have permission to view terminal parameters.')}
        </p>
      </section>
    );
  }

  const profile = profiles.data?.find((candidate) => candidate.id === profileId);
  const terminal = terminals.data?.find((candidate) => candidate.id === terminalId);
  const terminalProfile =
    terminal === undefined || terminal.profileId === null
      ? undefined
      : (profiles.data?.find((candidate) => candidate.id === terminal.profileId) ??
        terminal.profile ??
        undefined);

  let target: EditorTarget | null = null;
  if (scope === 'global') target = { scope: 'global' };
  else if (scope === 'profile' && profile !== undefined) {
    target = { scope: 'profile', profileId: profile.id, profileName: profile.name };
  } else if (scope === 'terminal' && terminal !== undefined) {
    target = {
      scope: 'terminal',
      terminalId: terminal.id,
      terminalLabel: `${terminal.code} · ${terminal.displayName}`,
      profileId: terminal.profileId,
      profileName: terminalProfile?.name ?? null,
    };
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('parameters.page.title', 'Parameters')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t(
              'parameters.page.intro',
              'Till behaviour, authored at global, profile or terminal scope. The most specific scope wins; anything not set runs on the registry default.',
            )}
          </p>
        </div>
        {/* RL-281 — JSON, not CSV: parameters are typed and sometimes nested
            (TP-FR-028). Authored values only, stamped with registry version
            and tenant (TP-FR-029). */}
        <div className="flex flex-col items-end gap-1">
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={exportParams.isPending}
              onClick={async () => {
                setExchangeError(null);
                try {
                  await exportParams.mutateAsync();
                } catch (cause) {
                  setExchangeError(extractApiError(cause));
                }
              }}
            >
              {exportParams.isPending
                ? t('parameters.page.exporting', 'Exporting…')
                : t('parameters.page.exportJson', 'Export JSON')}
            </Button>
            {canWrite && (
              <Button type="button" variant="outline" onClick={() => setImporting((open) => !open)}>
                {importing
                  ? t('parameters.page.closeImport', 'Close import')
                  : t('parameters.page.importJson', 'Import JSON…')}
              </Button>
            )}
            {/* RL-416 — the bytes behind `kitchen.orders.newOrderSoundFile`.
                Read-only operators can still open it to see what is on offer. */}
            <Button
              type="button"
              variant="outline"
              onClick={() => setManagingAudio((open) => !open)}
            >
              {managingAudio
                ? t('terminalAudio.closeManage', 'Close sounds')
                : t('terminalAudio.manage', 'Sounds…')}
            </Button>
          </div>
          {exchangeError !== null && <p className="text-xs text-destructive">{exchangeError}</p>}
        </div>
      </header>

      {importing && canWrite && <ImportPanel onClose={() => setImporting(false)} />}

      {managingAudio && (
        <TerminalAudioPanel canWrite={canWrite} onClose={() => setManagingAudio(false)} />
      )}

      <section className="flex flex-wrap items-end gap-4 rounded-lg border bg-card p-4">
        <div className="space-y-1">
          <Label htmlFor="param-scope">{t('parameters.page.scope', 'Scope')}</Label>
          <Select
            id="param-scope"
            className="w-40"
            value={scope}
            onChange={(event) => setScope(event.target.value as TerminalParamScope)}
          >
            <option value="global">{t('parameters.page.global', 'Global')}</option>
            <option value="profile">
              {t('parameters.page.terminalProfile', 'Terminal profile')}
            </option>
            <option value="terminal">{t('parameters.page.terminal', 'Terminal')}</option>
          </Select>
        </div>
        {scope === 'profile' && (
          <div className="space-y-1">
            <Label htmlFor="param-profile">{t('parameters.page.profile', 'Profile')}</Label>
            <Select
              id="param-profile"
              className="w-64"
              value={profileId}
              disabled={profiles.isLoading}
              onChange={(event) => setProfileId(event.target.value)}
            >
              <option value="">{t('parameters.page.chooseProfile', '— choose a profile —')}</option>
              {profiles.data?.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.name}
                </option>
              ))}
            </Select>
            {profiles.isError && (
              <p className="text-xs text-destructive">
                {t('parameters.page.profilesError', 'Could not load profiles.')}
              </p>
            )}
          </div>
        )}
        {scope === 'terminal' && (
          <div className="space-y-1">
            <Label htmlFor="param-terminal">{t('parameters.page.terminal', 'Terminal')}</Label>
            <Select
              id="param-terminal"
              className="w-72"
              value={terminalId}
              disabled={terminals.isLoading}
              onChange={(event) => setTerminalId(event.target.value)}
            >
              <option value="">
                {t('parameters.page.chooseTerminal', '— choose a terminal —')}
              </option>
              {terminals.data?.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.code} · {candidate.displayName}
                </option>
              ))}
            </Select>
            {terminals.isError && (
              <p className="text-xs text-destructive">
                {t('parameters.page.terminalsError', 'Could not load terminals.')}
              </p>
            )}
          </div>
        )}
      </section>

      {target === null ? (
        <p className="text-sm text-muted-foreground">
          {scope === 'profile'
            ? t(
                'parameters.page.pickProfile',
                'Choose a profile to edit the parameters every terminal on it inherits.',
              )
            : t(
                'parameters.page.pickTerminal',
                'Choose a terminal to edit the parameters set on that till alone.',
              )}
        </p>
      ) : (
        <ParameterEditor target={target} attention="estate" />
      )}
    </div>
  );
}
